import { useState, type ChangeEvent } from 'react'
import { Link } from '@tanstack/react-router'
import {
  executeImport,
  uploadYnabFile,
  type ImportPreview,
  type ImportResult,
} from './api'

type Stage = 'pick' | 'uploading' | 'preview' | 'importing' | 'done'

export function YnabImportScreen() {
  const [stage, setStage] = useState<Stage>('pick')
  const [error, setError] = useState<string | null>(null)
  const [preview, setPreview] = useState<ImportPreview | null>(null)
  const [result, setResult] = useState<ImportResult | null>(null)

  async function onFile(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return
    setError(null)
    setStage('uploading')
    try {
      const p = await uploadYnabFile(file)
      setPreview(p)
      setStage('preview')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed')
      setStage('pick')
    }
  }

  async function onConfirm() {
    if (!preview) return
    setError(null)
    setStage('importing')
    try {
      const r = await executeImport(preview.importId, preview)
      setResult(r)
      setStage('done')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Import failed')
      setStage('preview')
    }
  }

  function reset() {
    setStage('pick')
    setPreview(null)
    setResult(null)
    setError(null)
  }

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <header className="flex items-baseline justify-between">
        <h1 className="text-2xl font-semibold">Import from YNAB</h1>
        <Link to="/settings" className="text-sm text-blue-600 hover:underline dark:text-blue-400">
          Back to settings
        </Link>
      </header>

      <p className="text-sm text-zinc-600 dark:text-zinc-400">
        Upload a YNAB export (.json from the YNAB API, or the .csv from
        &quot;Export budget&quot;). We&apos;ll preview what we found and let you
        confirm before anything is written.
      </p>

      {error && (
        <div role="alert" className="rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/50 dark:text-red-300">
          {error}
        </div>
      )}

      {stage === 'pick' && (
        <label className="flex cursor-pointer flex-col items-center justify-center gap-2 rounded-md border-2 border-dashed border-zinc-300 px-6 py-12 text-sm text-zinc-600 hover:border-zinc-400 dark:border-zinc-700 dark:text-zinc-400 dark:hover:border-zinc-600">
          <span className="font-medium">Choose file</span>
          <span className="text-xs">.json or .csv, max 10 MB</span>
          <input
            type="file"
            accept=".json,.csv,application/json,text/csv"
            onChange={onFile}
            className="sr-only"
          />
        </label>
      )}

      {stage === 'uploading' && <Stage label="Parsing your file…" />}

      {stage === 'preview' && preview && (
        <section className="space-y-4 rounded-md border border-zinc-200 p-4 dark:border-zinc-800">
          <div className="text-sm font-medium">{preview.fileName}</div>
          <SummaryGrid summary={preview.summary} />
          {preview.summary.dateRange?.earliest && (
            <div className="text-xs text-zinc-500">
              {preview.summary.dateRange.earliest} → {preview.summary.dateRange.latest}
            </div>
          )}
          <div className="flex gap-2 pt-2">
            <button
              type="button"
              onClick={onConfirm}
              className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
            >
              Import everything
            </button>
            <button
              type="button"
              onClick={reset}
              className="rounded-md border border-zinc-300 px-4 py-2 text-sm hover:bg-zinc-100 dark:border-zinc-700 dark:hover:bg-zinc-800"
            >
              Cancel
            </button>
          </div>
        </section>
      )}

      {stage === 'importing' && <Stage label="Importing your data… this can take a minute for large budgets." />}

      {stage === 'done' && result && (
        <section className="space-y-3 rounded-md border border-zinc-200 p-4 text-sm dark:border-zinc-800">
          <div className={`text-base font-semibold ${result.success ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}>
            {result.success ? 'Import complete' : 'Import finished with errors'}
          </div>
          <div className="grid grid-cols-3 gap-3">
            <Stat label="Imported" value={result.imported} />
            <Stat label="Skipped" value={result.skipped} />
            <Stat label="Failed" value={result.failed} />
          </div>
          {result.createdCategories.length > 0 && (
            <div className="text-xs text-zinc-500">
              Created {result.createdCategories.length} new categories,{' '}
              {result.createdAccounts.length} new accounts.
            </div>
          )}
          {result.errors.length > 0 && (
            <details className="text-xs">
              <summary className="cursor-pointer text-red-600 dark:text-red-400">
                {result.errors.length} error{result.errors.length === 1 ? '' : 's'}
              </summary>
              <ul className="ml-4 mt-1 list-disc space-y-1 text-zinc-600 dark:text-zinc-400">
                {result.errors.slice(0, 10).map((e, i) => (
                  <li key={i}>{e}</li>
                ))}
              </ul>
            </details>
          )}
          <button
            type="button"
            onClick={reset}
            className="rounded-md border border-zinc-300 px-4 py-2 text-sm hover:bg-zinc-100 dark:border-zinc-700 dark:hover:bg-zinc-800"
          >
            Import another file
          </button>
        </section>
      )}
    </div>
  )
}

function Stage({ label }: { label: string }) {
  return (
    <div className="flex items-center gap-3 rounded-md border border-zinc-200 px-4 py-3 text-sm text-zinc-600 dark:border-zinc-800 dark:text-zinc-400">
      <span className="inline-block h-3 w-3 animate-pulse rounded-full bg-blue-500" />
      {label}
    </div>
  )
}

function SummaryGrid({ summary }: { summary: ImportPreview['summary'] }) {
  return (
    <dl className="grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
      <Stat label="Accounts" value={summary.totalAccounts} />
      <Stat label="Categories" value={summary.totalCategories} />
      <Stat label="Payees" value={summary.totalPayees} />
      <Stat label="Transactions" value={summary.totalTransactions} />
    </dl>
  )
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-md bg-zinc-50 px-3 py-2 dark:bg-zinc-900">
      <div className="text-xs uppercase tracking-wide text-zinc-500">{label}</div>
      <div className="text-lg font-semibold">{value.toLocaleString()}</div>
    </div>
  )
}
